import {
  type InstallmentPaymentIneligibilityReason,
  normalizePhoneNumber,
} from '@massilia/contracts'
import { useAppForm } from '@massilia/features'
import { toError, useInitiateInstallmentPaymentMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { CalendarClock, Loader2 } from 'lucide-react'
import { useState } from 'react'

import type { FloaPayTypeFormValue } from '@/components/checkout/checkout-form.schema'

import { InstallmentsSubForm } from '@/components/checkout/-sections/installments-sub-form'
import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { parseInstallmentIneligibilityReason } from '@/lib/installment-payment-error'
import { installmentPaymentSchema } from '@/lib/schemas'
import { centsToEuros, formatPrice } from '@/utils/format-price'

interface DepositInstallmentsFormProps {
  /** Solde restant du dossier, en centimes : montant imposé, non modifiable. */
  amountCents: number
  credentials: { dossierNumber: string; email: string; lastName: string }
  /**
   * Le serveur a refusé l'opération pour un motif d'éligibilité connu (l'éligibilité a
   * changé depuis l'affichage du formulaire) : la page bascule alors sur le paiement CB.
   */
  onIneligible: (reason: InstallmentPaymentIneligibilityReason) => void
  /** Prestataire courant du paiement en plusieurs fois (flag runtime serveur) */
  provider: 'FLOA' | 'SSP'
}

/**
 * Règlement du solde restant en 3 ou 4 fois, hors tunnel de réservation.
 *
 * Le montant n'est pas saisissable : c'est le solde du dossier, recalculé par le serveur.
 * Les champs de facturation sont ceux du tunnel (`InstallmentsSubForm`), pour que les deux
 * parcours envoient exactement les mêmes données au prestataire.
 */
export function DepositInstallmentsForm({
  amountCents,
  credentials,
  onIneligible,
  provider,
}: DepositInstallmentsFormProps) {
  const { mutateAsync: initiateInstallmentPayment } = useInitiateInstallmentPaymentMutation()
  const [submitError, setSubmitError] = useState<null | string>(null)

  const providerLabel = provider === 'FLOA' ? 'FLOA' : 'Oney'

  const form = useAppForm({
    defaultValues: {
      billingCity: '',
      billingCountryCode: { label: 'France', value: 'FR' },
      billingStreet: '',
      billingZipCode: '',
      floaPayType: '3XG' as FloaPayTypeFormValue,
      phone: '',
    },
    onSubmit: async ({ value }) => {
      setSubmitError(null)

      try {
        const result = await initiateInstallmentPayment({
          email: credentials.email,
          lastName: credentials.lastName,
          billing: {
            city: value.billingCity.trim(),
            countryCode: value.billingCountryCode.value,
            street: value.billingStreet.trim(),
            zipCode: value.billingZipCode.trim(),
          },
          dossierNumber: credentials.dossierNumber,
          // Toujours transmis, même quand on croit être sous SSP : le serveur ne le lit que
          // si le prestataire courant est FLOA, et le taire sur un désaccord front/serveur
          // (requête de flag en échec, bascule du flag pendant la saisie) coûterait un refus
          // « rechargez la page » dont recharger ne sort pas.
          floaPayType: value.floaPayType,
          // Même normalisation que le tunnel : SSP refuse les numéros mal formés.
          phone: normalizePhoneNumber(value.phone) ?? value.phone.trim(),
        })
        window.location.assign(result.redirectUrl)
      } catch (error) {
        const reason = parseInstallmentIneligibilityReason(toError(error))
        if (reason) {
          onIneligible(reason)
          return
        }
        setSubmitError(resolveAuthErrorMessage(toError(error)))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          installmentPaymentSchema
        ),
    },
  })

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      {/* Montant imposé : le fractionné porte sur la totalité du solde restant. */}
      <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-light-200 bg-white p-4">
        <div>
          <div className="text-sm font-medium text-gray-light-700">Montant à régler en 3/4x</div>
          <div className="text-xs text-gray-light-500">
            Il correspond au solde restant de votre dossier et n&apos;est pas modifiable.
          </div>
        </div>
        <div className="text-lg font-bold text-gray-light-900">
          {formatPrice(centsToEuros(amountCents))}
        </div>
      </div>

      <form.AppField name="phone">
        {(field) => <field.PhoneNumberField label="Téléphone" />}
      </form.AppField>

      <InstallmentsSubForm
        fields={{
          billingCity: 'billingCity',
          billingCountryCode: 'billingCountryCode',
          billingStreet: 'billingStreet',
          billingZipCode: 'billingZipCode',
          floaPayType: 'floaPayType',
        }}
        form={form}
        provider={provider}
      />

      {submitError && (
        <p className="text-sm font-medium text-red-600" role="alert">
          {submitError}
        </p>
      )}

      <form.Subscribe selector={(state) => state.isSubmitting}>
        {(isSubmitting) => (
          <Button
            className="w-full bg-brand-600 px-4 py-3 text-base font-medium text-white hover:bg-brand-700"
            disabled={isSubmitting}
            type="submit"
          >
            {isSubmitting ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              <>
                <CalendarClock className="mr-2 size-4" />
                Payer en 3/4x avec {providerLabel} (avec frais)
              </>
            )}
          </Button>
        )}
      </form.Subscribe>
    </form>
  )
}
